import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import {
  Clock,
  Phone,
  Mail,
  MessageSquare,
  Plus,
  Trash2,
  Save,
  Globe
} from 'lucide-react'

interface BusinessHoursDay {
  enabled: boolean
  start: string
  end: string
}

interface BusinessHours {
  monday: BusinessHoursDay
  tuesday: BusinessHoursDay
  wednesday: BusinessHoursDay
  thursday: BusinessHoursDay
  friday: BusinessHoursDay
  saturday: BusinessHoursDay
  sunday: BusinessHoursDay
}

interface PreChatField {
  name: string
  label: string
  type: 'text' | 'email' | 'tel' | 'textarea' | 'select'
  required: boolean
  enabled: boolean
  options?: string[]
}

interface ChatSettings {
  business_hours: BusinessHours
  timezone: string
  sms_phone_numbers: string[]
  offline_email: string
  pre_chat_fields: PreChatField[]
  widget_enabled: boolean
  welcome_message: string
  offline_message: string
  widget_color: string
  auto_response_enabled: boolean
  auto_response_message: string
}

const defaultBusinessHours: BusinessHours = {
  monday: { enabled: true, start: '07:00', end: '22:00' },
  tuesday: { enabled: true, start: '07:00', end: '22:00' },
  wednesday: { enabled: true, start: '07:00', end: '22:00' },
  thursday: { enabled: true, start: '07:00', end: '22:00' },
  friday: { enabled: true, start: '07:00', end: '22:00' },
  saturday: { enabled: true, start: '07:00', end: '22:00' },
  sunday: { enabled: true, start: '07:00', end: '22:00' }
}

const defaultPreChatFields: PreChatField[] = [
  { name: 'name', label: 'Name', type: 'text', required: true, enabled: true },
  { name: 'email', label: 'Email', type: 'email', required: true, enabled: true },
  { name: 'phone', label: 'Phone Number', type: 'tel', required: true, enabled: true },
  { name: 'company', label: 'Company/Brokerage', type: 'text', required: false, enabled: false },
  { name: 'monthly_volume', label: 'Monthly Volume', type: 'text', required: false, enabled: false },
  { name: 'message', label: 'How can we help?', type: 'textarea', required: false, enabled: true }
]

const ChatSettings: React.FC = () => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState<ChatSettings>({
    business_hours: defaultBusinessHours,
    timezone: 'America/New_York',
    sms_phone_numbers: [],
    offline_email: 'support@mortgageflow.com',
    pre_chat_fields: defaultPreChatFields,
    widget_enabled: true,
    welcome_message: 'Hi! How can we help you today?',
    offline_message: "We're currently offline. Leave us a message and we'll get back to you!",
    widget_color: '#3B82F6',
    auto_response_enabled: true,
    auto_response_message: "Thanks for reaching out! We'll be with you shortly."
  })

  const [newPhoneNumber, setNewPhoneNumber] = useState('')
  const [newFieldName, setNewFieldName] = useState('')
  const [newFieldLabel, setNewFieldLabel] = useState('')
  const [newFieldType, setNewFieldType] = useState<'text' | 'email' | 'tel' | 'textarea'>('text')

  const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const response = await makeApiCall('/api/chat/settings')
      const data = await response.json()
      if (data.settings) {
        setSettings({
          ...data.settings,
          business_hours: typeof data.settings.business_hours === 'string'
            ? JSON.parse(data.settings.business_hours)
            : data.settings.business_hours,
          sms_phone_numbers: typeof data.settings.sms_phone_numbers === 'string'
            ? JSON.parse(data.settings.sms_phone_numbers)
            : data.settings.sms_phone_numbers,
          pre_chat_fields: typeof data.settings.pre_chat_fields === 'string'
            ? JSON.parse(data.settings.pre_chat_fields)
            : data.settings.pre_chat_fields
        })
      }
    } catch (error) {
      console.error('Error fetching chat settings:', error)
      alert('Failed to load chat settings')
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    try {
      setSaving(true)
      await makeJsonApiCall('/api/chat/settings', 'PUT', settings)
      alert('Chat settings saved successfully')
    } catch (error) {
      console.error('Error saving chat settings:', error)
      alert('Failed to save chat settings')
    } finally {
      setSaving(false)
    }
  }

  const updateBusinessHours = (day: string, field: 'enabled' | 'start' | 'end', value: boolean | string) => {
    setSettings({
      ...settings,
      business_hours: {
        ...settings.business_hours,
        [day]: {
          ...settings.business_hours[day as keyof BusinessHours],
          [field]: value
        }
      }
    })
  }

  const addPhoneNumber = () => {
    if (newPhoneNumber.trim()) {
      setSettings({
        ...settings,
        sms_phone_numbers: [...settings.sms_phone_numbers, newPhoneNumber.trim()]
      })
      setNewPhoneNumber('')
    }
  }

  const removePhoneNumber = (index: number) => {
    setSettings({
      ...settings,
      sms_phone_numbers: settings.sms_phone_numbers.filter((_, i) => i !== index)
    })
  }

  const addCustomField = () => {
    if (newFieldName && newFieldLabel) {
      const field: PreChatField = {
        name: newFieldName.toLowerCase().replace(/\s+/g, '_'),
        label: newFieldLabel,
        type: newFieldType,
        required: false,
        enabled: true
      }
      setSettings({
        ...settings,
        pre_chat_fields: [...settings.pre_chat_fields, field]
      })
      setNewFieldName('')
      setNewFieldLabel('')
      setNewFieldType('text')
    }
  }

  const updateField = (index: number, updates: Partial<PreChatField>) => {
    const updatedFields = [...settings.pre_chat_fields]
    updatedFields[index] = { ...updatedFields[index], ...updates }
    setSettings({ ...settings, pre_chat_fields: updatedFields })
  }

  const removeField = (index: number) => {
    setSettings({
      ...settings,
      pre_chat_fields: settings.pre_chat_fields.filter((_, i) => i !== index)
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Live Chat Settings</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Configure your live chat widget and team notifications
          </p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Business Hours */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Business Hours</h2>
        </div>
        <div className="space-y-3">
          {daysOfWeek.map(day => (
            <div key={day} className="flex items-center gap-4">
              <input
                type="checkbox"
                checked={settings.business_hours[day as keyof BusinessHours].enabled}
                onChange={(e) => updateBusinessHours(day, 'enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600"
              />
              <span className="w-24 text-sm font-medium text-gray-700 dark:text-gray-300 capitalize">
                {day}
              </span>
              <input
                type="time"
                value={settings.business_hours[day as keyof BusinessHours].start}
                onChange={(e) => updateBusinessHours(day, 'start', e.target.value)}
                disabled={!settings.business_hours[day as keyof BusinessHours].enabled}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
              />
              <span className="text-gray-500 dark:text-gray-400">to</span>
              <input
                type="time"
                value={settings.business_hours[day as keyof BusinessHours].end}
                onChange={(e) => updateBusinessHours(day, 'end', e.target.value)}
                disabled={!settings.business_hours[day as keyof BusinessHours].enabled}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
              />
            </div>
          ))}
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Globe className="w-4 h-4 inline mr-1" />
            Timezone
          </label>
          <select
            value={settings.timezone}
            onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
            <option value="America/Toronto">Toronto</option>
            <option value="America/Vancouver">Vancouver</option>
          </select>
        </div>
      </div>

      {/* Phone Numbers for SMS Notifications */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Phone className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">SMS Notifications</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Team members will receive SMS alerts when new chat requests come in
        </p>
        <div className="space-y-2">
          {settings.sms_phone_numbers.map((phone, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={phone}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              <button
                onClick={() => removePhoneNumber(index)}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <input
              type="tel"
              value={newPhoneNumber}
              onChange={(e) => setNewPhoneNumber(e.target.value)}
              placeholder="+1 234 567 8900"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              onClick={addPhoneNumber}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Offline Email */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Offline Messages</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Email address to receive offline messages
        </p>
        <input
          type="email"
          value={settings.offline_email}
          onChange={(e) => setSettings({ ...settings, offline_email: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>

      {/* Pre-Chat Form Fields */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pre-Chat Form Fields</h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Customize the information you collect before starting a chat
        </p>
        <div className="space-y-2 mb-4">
          {settings.pre_chat_fields.map((field, index) => (
            <div key={index} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <input
                type="checkbox"
                checked={field.enabled}
                onChange={(e) => updateField(index, { enabled: e.target.checked })}
                className="w-4 h-4 text-blue-600"
              />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-white">{field.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {field.type} • {field.required ? 'Required' : 'Optional'}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => updateField(index, { required: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                Required
              </label>
              {!['name', 'email', 'phone'].includes(field.name) && (
                <button
                  onClick={() => removeField(index)}
                  className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {/* Add Custom Field */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Add Custom Field</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <input
              type="text"
              value={newFieldName}
              onChange={(e) => setNewFieldName(e.target.value)}
              placeholder="Field name"
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <input
              type="text"
              value={newFieldLabel}
              onChange={(e) => setNewFieldLabel(e.target.value)}
              placeholder="Field label"
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <select
              value={newFieldType}
              onChange={(e) => setNewFieldType(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="text">Text</option>
              <option value="email">Email</option>
              <option value="tel">Phone</option>
              <option value="textarea">Textarea</option>
            </select>
            <button
              onClick={addCustomField}
              disabled={!newFieldName || !newFieldLabel}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              Add Field
            </button>
          </div>
        </div>
      </div>

      {/* Widget Messages */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Widget Messages</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Welcome Message (Online)
            </label>
            <input
              type="text"
              value={settings.welcome_message}
              onChange={(e) => setSettings({ ...settings, welcome_message: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Offline Message
            </label>
            <input
              type="text"
              value={settings.offline_message}
              onChange={(e) => setSettings({ ...settings, offline_message: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <input
                type="checkbox"
                checked={settings.auto_response_enabled}
                onChange={(e) => setSettings({ ...settings, auto_response_enabled: e.target.checked })}
                className="w-4 h-4 text-blue-600"
              />
              Enable Auto-Response
            </label>
            {settings.auto_response_enabled && (
              <input
                type="text"
                value={settings.auto_response_message}
                onChange={(e) => setSettings({ ...settings, auto_response_message: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatSettings

